import React, { useState, useMemo } from 'react';
import { RateSelector } from './RateSelector';
import { InputCard, InputRow } from './InputCard';
import { InfoTooltip } from './InfoTooltip';
import { MortgageRate, CreditScoreTier } from '../types';
import { creditSpreads } from '../data/rates';
import { RateBuyDownOptimizer } from './RateBuyDownOptimizer';
import { useMortgageRates } from '../hooks/useMortgageRates';

interface CostInputs {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
  creditScore: CreditScoreTier;
  
  // Section A
  originationCharges: number; // Points + Underwriting + Processing
  
  // Section B, C, E
  servicesAndGovFees: number; // Appraisal, Title, Recording, Transfer Taxes
  
  // Section F, G
  prepaidsAndEscrows: number; // Insurance, Property Taxes, Prepaid Interest
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatPercent = (value: number) => {
    return `${value.toFixed(3)}%`;
};

// APR Calculation
const calculateAPR = (loanAmount: number, interestRate: number, termYears: number, totalClosingCosts: number) => {
    if (loanAmount <= 0 || termYears <= 0) return 0;
    
    const monthlyRate = interestRate / 100 / 12;
    const months = termYears * 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const financedAmount = loanAmount - totalClosingCosts;

    // Binary search for APR
    let low = 0;
    let high = 1; 
    let guess = monthlyRate;
    
    for(let i=0; i<30; i++) {
        guess = (low + high) / 2;
        const calcPmt = financedAmount * (guess * Math.pow(1+guess, months)) / (Math.pow(1+guess, months) - 1);
        if (calcPmt > monthlyPayment) high = guess;
        else low = guess;
    }
    
    return guess * 12 * 100;
};

export const MortgageCostEstimator: React.FC = () => {
  const { rates, lastUpdated } = useMortgageRates();

  const [inputs, setInputs] = useState<CostInputs>({
    loanAmount: 400000,
    interestRate: 6.26,
    loanTerm: 30,
    creditScore: CreditScoreTier.EXCELLENT,
    originationCharges: 1500,
    servicesAndGovFees: 3500,
    prepaidsAndEscrows: 1200,
  });

  const handleRateSelect = (rate: MortgageRate) => {
    setInputs(prev => ({
      ...prev,
      interestRate: rate.rate,
      loanTerm: rate.term,
    }));
  };

  // Synchronize credit score from RateSelector
  const handleCreditScoreChange = (tier: CreditScoreTier) => {
      setInputs(prev => ({ ...prev, creditScore: tier }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setInputs(prev => {
        if (name === 'creditScore') {
            return { ...prev, [name]: value as CreditScoreTier };
        }
        const numValue = parseFloat(value) || 0;
        return { ...prev, [name]: numValue };
    });
  };

  const { 
      apr, 
      totalLoanCosts, 
      totalCashToClose, 
      monthlyPayment 
  } = useMemo(() => {
      const totalLoanCosts = inputs.originationCharges + inputs.servicesAndGovFees;
      const totalCashToClose = totalLoanCosts + inputs.prepaidsAndEscrows; // Simplified
      const apr = calculateAPR(inputs.loanAmount, inputs.interestRate, inputs.loanTerm, totalLoanCosts);
      
      const r = inputs.interestRate / 100 / 12;
      const n = inputs.loanTerm * 12;
      const monthlyPayment = inputs.loanAmount * (r * Math.pow(1+r, n)) / (Math.pow(1+r, n) - 1);

      return { apr, totalLoanCosts, totalCashToClose, monthlyPayment };
  }, [inputs]);

  // Market Benchmarking
  const benchmarks = useMemo(() => {
      const baseRateObj = rates.find(r => r.term === inputs.loanTerm) || rates[0];
      
      // Context-Aware Adjustment:
      const creditAdjustment = creditSpreads[inputs.creditScore];
      
      // The "Benchmark" average rate is specifically calculated for this credit tier.
      const avgRate = parseFloat((baseRateObj.rate + creditAdjustment).toFixed(3));

      // Assumptions for Costs Calculation (Approximations)
      // Methodology: 1% Origination + $3000 Fixed Fees (Title, Appraisal, Recording)
      const avgOrigination = inputs.loanAmount * 0.01;
      const avgFixedFees = 3000;
      const avgCosts = avgOrigination + avgFixedFees;
      
      // Top 25%: Strong deal. 0.5% Origination fee + slightly lower rate.
      const top25Rate = parseFloat((avgRate - 0.125).toFixed(3));
      const top25Costs = (inputs.loanAmount * 0.005) + 2500;

      // Top 5% (The "Holy Grail"): 0% Origination Fee + Competitive Rate.
      // We raised the cost floor to $2500 (Title/Gov fees are hard to avoid)
      // We relaxed the rate slightly to -0.25% because usually 0% origination means rate is slightly higher.
      const top5Rate = parseFloat((avgRate - 0.25).toFixed(3));
      const top5Costs = 2500; 

      const avgAPR = calculateAPR(inputs.loanAmount, avgRate, inputs.loanTerm, avgCosts);
      const top25APR = calculateAPR(inputs.loanAmount, top25Rate, inputs.loanTerm, top25Costs);
      const top5APR = calculateAPR(inputs.loanAmount, top5Rate, inputs.loanTerm, top5Costs);

      // Assessment Logic
      let status = "Average";
      let colorClass = "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-100";
      let message = "";

      // Determine simple credit label for display
      const creditLabel = inputs.creditScore.split(' ')[0]; // "Excellent", "Good", "Fair", "Poor"

      // We use a small buffer (0.05) to avoid floating point strictness
      if (apr <= top5APR + 0.05) {
          status = "Exceptional (Top 5%)";
          colorClass = "bg-green-100 dark:bg-green-900/40 border-green-400 text-green-800 dark:text-green-100";
          message = `Comparing against the modeled rates for borrowers with ${creditLabel} credit, this quote is in the top 5% of the market.`;
      } else if (apr <= top25APR + 0.05) {
          status = "Great (Top 25%)";
          colorClass = "bg-blue-100 dark:bg-blue-900/40 border-blue-400 text-blue-800 dark:text-blue-100";
          message = `This quote's APR is lower than the modeled average offer for borrowers with ${creditLabel} credit.`;
      } else if (apr <= avgAPR + 0.25) {
          status = "Average";
          colorClass = "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-100";
          message = `This quote is in line with current market averages for borrowers with ${creditLabel} credit.`;
      } else {
          status = "Above Average Cost";
          colorClass = "bg-red-100 dark:bg-red-900/40 border-red-400 text-red-800 dark:text-red-100";
          message = `This quote's APR is higher than the modeled average for borrowers with ${creditLabel} credit. You may want to shop around for a lower rate.`;
      }

      return {
          avg: { rate: avgRate, costs: avgCosts, apr: avgAPR },
          top25: { rate: top25Rate, costs: top25Costs, apr: top25APR },
          top5: { rate: top5Rate, costs: top5Costs, apr: top5APR },
          assessment: { status, colorClass, message, creditLabel },
          methodology: { avgOrigination, avgFixedFees }
      };
  }, [inputs.loanAmount, inputs.loanTerm, inputs.creditScore, apr, rates]);

  const inputClass = "w-full p-2 border rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white transition-colors focus:ring-2 focus:ring-bp-light-blue outline-none";

  return (
    <div>
      <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-bp-slate dark:text-white">Mortgage Cost Estimator</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">Compare your official Loan Estimate against market benchmarks.</p>
      </header>

      <RateSelector 
        onRateSelect={handleRateSelect} 
        selectedCreditTier={inputs.creditScore}
        onCreditTierChange={handleCreditScoreChange}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        {/* Left Column: Inputs */}
        <div className="lg:col-span-1 flex flex-col gap-6">
             <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    How to use this tool
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                    Grab your official <strong>Loan Estimate</strong> (LE) document from your lender. Enter the figures from Sections A, B, C, E, F, and G into the fields below to see how your deal stacks up.
                </p>
            </div>

            <InputCard title="1. Loan Details">
                <InputRow label="Loan Amount" id="loanAmount">
                    <div className="flex items-center">
                         <span className="text-gray-500 dark:text-gray-400 mr-1">$</span>
                        <input type="number" id="loanAmount" name="loanAmount" value={inputs.loanAmount} onChange={handleChange} className={inputClass} />
                        <InfoTooltip text="The total amount you are borrowing. Found on Page 1 of your Loan Estimate under 'Loan Amount'." />
                    </div>
                </InputRow>
                <InputRow label="Interest Rate" id="interestRate">
                    <div className="flex items-center">
                        <input type="number" step="0.001" id="interestRate" name="interestRate" value={inputs.interestRate} onChange={handleChange} className={inputClass} />
                        <span className="text-gray-500 dark:text-gray-400 ml-1">%</span>
                        <InfoTooltip text="The interest rate listed on Page 1 of your Loan Estimate. This is not the APR." />
                    </div>
                </InputRow>
                 <InputRow label="Loan Term" id="loanTerm">
                    <div className="flex items-center">
                        <input type="number" id="loanTerm" name="loanTerm" value={inputs.loanTerm} onChange={handleChange} className={inputClass} />
                        <span className="text-gray-500 dark:text-gray-400 ml-1">yrs</span>
                        <InfoTooltip text="The duration of your loan (e.g., 30 years). Found on Page 1." />
                    </div>
                </InputRow>
                <InputRow label="Credit Score" id="creditScore">
                    <div className="flex items-center">
                        <select id="creditScore" name="creditScore" value={inputs.creditScore} onChange={handleChange} className={inputClass}>
                             {Object.values(CreditScoreTier).map(tier => (
                                <option key={tier} value={tier}>{tier}</option>
                            ))}
                        </select>
                        <InfoTooltip text="Select your credit score range. This is CRITICAL to ensure we compare your quote against the correct market averages for your risk profile." />
                    </div>
                </InputRow>
            </InputCard>
            
            <InputCard title="2. Closing Costs">
                <InputRow label="Origination Charges (Sec A)" id="originationCharges">
                     <div className="flex items-center">
                        <span className="text-gray-500 dark:text-gray-400 mr-1">$</span>
                        <input type="number" id="originationCharges" name="originationCharges" value={inputs.originationCharges} onChange={handleChange} className={inputClass} />
                        <InfoTooltip text="Sum of all items in Section A (Page 2). Includes Points, Application Fee, Underwriting Fee, Processing Fee." />
                    </div>
                </InputRow>
                 <InputRow label="Services & Gov Fees (B+C+E)" id="servicesAndGovFees">
                     <div className="flex items-center">
                        <span className="text-gray-500 dark:text-gray-400 mr-1">$</span>
                        <input type="number" id="servicesAndGovFees" name="servicesAndGovFees" value={inputs.servicesAndGovFees} onChange={handleChange} className={inputClass} />
                        <InfoTooltip text="Sum of Sections B (Services You Cannot Shop For), C (Services You Can Shop For), and E (Taxes and Other Government Fees). Examples: Appraisal, Title Insurance, Recording Fees." />
                    </div>
                </InputRow>
                <InputRow label="Prepaids & Escrows (F+G)" id="prepaidsAndEscrows">
                    <div className="flex items-center">
                        <span className="text-gray-500 dark:text-gray-400 mr-1">$</span>
                        <input type="number" id="prepaidsAndEscrows" name="prepaidsAndEscrows" value={inputs.prepaidsAndEscrows} onChange={handleChange} className={inputClass} />
                        <InfoTooltip text="Sum of Sections F (Prepaids) and G (Initial Escrow Payment). Includes Homeowners Insurance premium, Prepaid Interest, and Property Tax reserves." />
                    </div>
                </InputRow>
            </InputCard>
        </div>

        {/* Right Column: Analysis */}
        <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
                <div className="flex justify-between items-center mb-6 border-b dark:border-slate-600 pb-4 flex-wrap gap-2">
                    <h3 className="text-2xl font-bold text-bp-slate dark:text-white">
                        Quote Assessment: <span className="text-bp-dark-blue dark:text-bp-light-blue">{benchmarks.assessment.creditLabel} Credit</span>
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded-full border border-gray-200 dark:border-slate-600">
                        Rates as of {lastUpdated}
                    </span>
                </div>
                
                <div className={`p-6 rounded-lg mb-8 border-l-8 ${benchmarks.assessment.colorClass} shadow-sm relative`}>
                    <h4 className="text-xl font-bold mb-2">{benchmarks.assessment.status}</h4>
                    <p className="text-base opacity-90">{benchmarks.assessment.message}</p>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-slate-600">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wider border-b border-gray-200 dark:border-slate-600">
                                <th className="p-4 font-bold">Metric</th>
                                <th className="p-4 text-right font-bold text-bp-dark-blue dark:text-bp-light-blue bg-blue-50 dark:bg-blue-900/30 border-l border-blue-100 dark:border-blue-800">Your Quote</th>
                                <th className="p-4 text-right font-semibold hidden sm:table-cell">
                                    Avg ({benchmarks.assessment.creditLabel})
                                </th>
                                <th className="p-4 text-right font-semibold text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                                    Top 25% ({benchmarks.assessment.creditLabel})
                                </th>
                                <th className="p-4 text-right font-bold text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border-l border-green-100 dark:border-green-800">Top 5% (Goal)</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-700 dark:text-gray-300">
                            <tr className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="p-4 font-medium">Interest Rate</td>
                                <td className="p-4 text-right font-bold text-bp-dark-blue dark:text-bp-light-blue bg-blue-50/50 dark:bg-blue-900/20 border-l border-blue-100 dark:border-blue-800">{formatPercent(inputs.interestRate)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatPercent(benchmarks.avg.rate)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatPercent(benchmarks.top25.rate)}</td>
                                <td className="p-4 text-right font-bold text-green-700 dark:text-green-400 bg-green-50/50 dark:bg-green-900/20 border-l border-green-100 dark:border-green-800">{formatPercent(benchmarks.top5.rate)}</td>
                            </tr>
                            <tr className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="p-4 font-medium flex items-center">
                                    APR
                                    <InfoTooltip text="Annual Percentage Rate. This is the best single number to compare loans because it includes both the interest rate and the lender fees." />
                                </td>
                                <td className="p-4 text-right font-bold text-bp-dark-blue dark:text-bp-light-blue bg-blue-50/50 dark:bg-blue-900/20 border-l border-blue-100 dark:border-blue-800">{formatPercent(apr)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatPercent(benchmarks.avg.apr)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatPercent(benchmarks.top25.apr)}</td>
                                <td className="p-4 text-right font-bold text-green-700 dark:text-green-400 bg-green-50/50 dark:bg-green-900/20 border-l border-green-100 dark:border-green-800">{formatPercent(benchmarks.top5.apr)}</td>
                            </tr>
                            <tr className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="p-4 font-medium flex items-center">
                                    Est. Loan Costs
                                    <InfoTooltip text="The sum of Origination Charges and Services (Sections A+B+C+E). Does not include Prepaids/Escrows which are roughly the same regardless of lender." />
                                </td>
                                <td className="p-4 text-right font-bold text-bp-dark-blue dark:text-bp-light-blue bg-blue-50/50 dark:bg-blue-900/20 border-l border-blue-100 dark:border-blue-800">{formatCurrency(totalLoanCosts)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatCurrency(benchmarks.avg.costs)}</td>
                                <td className="p-4 text-right hidden sm:table-cell">{formatCurrency(benchmarks.top25.costs)}</td>
                                <td className="p-4 text-right font-bold text-green-700 dark:text-green-400 bg-green-50/50 dark:bg-green-900/20 border-l border-green-100 dark:border-green-800">{formatCurrency(benchmarks.top5.costs)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <div className="mt-6 bg-gray-50 dark:bg-slate-700 p-4 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600">
                    <h5 className="font-bold text-gray-800 dark:text-gray-100 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                        Benchmark Methodology & Reality Check
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">How we calculate "Average":</p>
                            <ul className="list-disc pl-5 space-y-1 mb-3">
                                <li><strong>Rate:</strong> Base Market Rate + Credit Score Adj.</li>
                                <li><strong>Origination Fee:</strong> 1.0% of Loan Amount</li>
                                <li><strong>3rd Party Fees:</strong> {formatCurrency(benchmarks.methodology.avgFixedFees)} (Title, Appraisal, Gov)</li>
                            </ul>
                        </div>
                        <div className="bg-white dark:bg-slate-600 p-3 rounded border border-gray-200 dark:border-slate-500">
                            <p className="font-semibold text-green-800 dark:text-green-300 mb-1">Is "Top 5%" Realistic?</p>
                            <p className="mb-1">Yes, but it requires shopping. A Top 5% deal typically represents:</p>
                            <ul className="list-disc pl-5 space-y-1 text-green-900 dark:text-green-200">
                                <li><strong>$0 Lender/Origination Fees</strong></li>
                                <li><strong>Very Competitive Par Rate</strong> (-0.25% vs Avg)</li>
                                <li><strong>Lean 3rd Party Fees</strong> (~$2,500 total)</li>
                            </ul>
                        </div>
                    </div>
                    <p className="italic text-xs mt-2 text-gray-500 dark:text-gray-400">
                        <strong>Note:</strong> Transfer taxes (in states like FL, NY, PA, MD) are mandatory government fees that can add thousands to your costs. Our benchmarks exclude these variable local taxes to keep the comparison fair on lender-controlled fees.
                    </p>
                </div>
            </div>
            
            <RateBuyDownOptimizer 
                loanAmount={inputs.loanAmount} 
                interestRate={inputs.interestRate} 
                loanTerm={inputs.loanTerm} 
            />
        </div>
      </div>

      {/* Call To Action Widget */}
      <div className="mt-12 bg-bp-slate dark:bg-slate-800 text-white rounded-xl p-8 shadow-lg border border-bp-dark-blue dark:border-slate-600 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
            {/* Background decorative circle */}
            <div className="absolute -left-10 -bottom-20 w-60 h-60 bg-bp-dark-blue rounded-full opacity-10 pointer-events-none"></div>
            
            <div className="flex-1 z-10">
                <div className="flex items-center gap-3 mb-3">
                     <div className="bg-bp-light-blue/20 p-2 rounded-lg text-bp-light-blue border border-bp-light-blue/30">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                     </div>
                    <h3 className="text-2xl font-bold">Done analyzing? Start shopping.</h3>
                </div>
                <p className="text-gray-300 dark:text-gray-400 text-lg leading-relaxed max-w-2xl">
                    Don't settle for average rates. Connect with investor-friendly lenders on BiggerPockets who understand your financial goals and can compete for your business.
                </p>
            </div>
            <a 
                href="https://www.biggerpockets.com/business/finder/lenders" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white dark:bg-slate-600 text-bp-slate dark:text-white hover:bg-blue-50 dark:hover:bg-slate-500 font-bold py-4 px-8 rounded-lg text-lg transition-all hover:-translate-y-0.5 hover:shadow-xl shadow-lg whitespace-nowrap group z-10 min-w-[200px]"
            >
                Talk to a Lender
                <svg className="w-5 h-5 ml-2 text-bp-dark-blue dark:text-white group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
            </a>
      </div>
    </div>
  );
};